<template>
  <div class="sp-input-range-slider">
    <div class="sp-input-range-slider__track-container">
      <div class="sp-input-range-slider__track-background" />
      <div class="sp-input-range-slider__track-fill" :style="fillWidth" />
      <div v-if="props.showTicks" id="markers" class="sp-input-range-slider__ticks">
        <div v-for="tick in tickCount" :key="tick" class="sp-input-range-slider__tick"></div>
      </div>
      <input
        ref="range"
        class="sp-input-range-slider__input"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :value="model"
        @input="onInput"
      />
    </div>
    <slot name="append" />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  value: { type: Number, default: 0 },
  min: { type: Number, default: 1 },
  max: { type: Number, default: 12 },
  step: { type: Number, default: 1 },
  showTicks: { type: Boolean, default: true },
  tickSize: { type: [String, Number], default: "8px" },
});

const emit = defineEmits(["input"]);

const model = ref(props.value);
watch(model, () => emit("input", model.value));
watch(
  () => props.value,
  (value) => (model.value = value),
);

const onInput = (event) => {
  model.value = Number(event.target.value);
};

const fillWidth = computed(() => {
  const range = props.max - props.min;

  if (range === 0) {
    return 0;
  }

  const value = model.value - props.min;

  // Clamp the percentage value between 0% and 100%,
  // in case `value` is below 0 or exceeds the defined `range`.
  const computedWidth = Math.max(0, Math.min((value / range) * 100, 100));
  // Return the width as a CSS style string
  return `width: ${computedWidth}%;`;
});

const tickCount = computed(() => Math.floor((props.max - props.min) / props.step) + 1);
</script>

<style>
:host {
  --track-size: var(--sp-input-range-slider-track-size, 6px);
  --track-color: var(--sp-input-range-slider-track-color, #ccc);
  --track-border-radius: var(--sp-input-range-slider-track-border-radius, calc(var(--track-size) / 2));
  --track-fill-color: var(--sp-input-range-slider-track-fill-color, var(--sp-sys-color-primary, #1976d2));
  --track-hover-cursor: var(--sp-input-range-slider-track-hover-cursor, pointer);
  --thumb-size: var(--sp-input-range-slider-thumb-size, 16px);
  --thumb-color: var(--sp-input-range-slider-thumb-color, var(--sp-sys-color-primary, #1976d2));
  --thumb-border-color: var(--sp-input-range-slider-thumb-border-color, transparent);
  --thumb-border-width: var(--sp-input-range-slider-thumb-border-width, 0px);
  --thumb-border-radius: var(--sp-input-range-slider-thumb-border-radius, 50%);

  display: block;
}
</style>

<style scoped lang="scss">
.sp-input-range-slider {
  display: flex;
  align-items: center;
  gap: 8px;

  &__track-container {
    position: relative;
    width: 100%;
    height: var(--track-size);
  }

  &__track-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--track-color);
    border-radius: var(--track-border-radius);
    z-index: 0;
  }

  &__track-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: var(--track-fill-color);
    border-radius: var(--track-border-radius) 0 0 var(--track-border-radius);
    z-index: 1;

    // Make sure the fill width is dynamically calculated from the thumb’s center to avoid layout gaps or visual overlaps
    min-width: calc(var(--thumb-size) / 2);
    max-width: calc(100% - var(--thumb-size) / 2);
  }

  &__input {
    display: block;
    appearance: none;
    width: 100%;
    height: var(--track-size);
    background-color: transparent;
    outline: none;
    position: absolute;
    top: 0;
    z-index: 3;
    padding: 0;
    margin: 0;

    &:hover {
      cursor: var(--track-hover-cursor);
    }

    &::-webkit-slider-runnable-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-webkit-slider-thumb {
      appearance: none;
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
      margin-top: calc((var(--thumb-size) - var(--track-size)) / -2);
    }

    &::-moz-range-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-moz-range-thumb {
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
    }
  }

  &__ticks {
    position: absolute;
    top: 1px;
    left: calc(var(--thumb-size) / 2);
    width: calc(100% - var(--thumb-size));
    height: 0;
    display: flex;
    justify-content: space-between;
    pointer-events: none;
    z-index: 3;
  }

  &__tick {
    position: relative;
    width: 1px;
    background-color: var(--sp-input-range-slider-tick-color, #fff);
    height: 3px;
  }
}
</style>
